import React, { useRef, Suspense, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  Html,
  useHelper,
  Center,
  CameraControls,
  Environment,
  Lightformer,
  Preload,
  isWebGL2Available,
} from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import "./App.css";
import * as THREE from "three";
import { Model } from "./Test_10";
import { start } from "repl";
import { Diagram } from "./Diagram";
import GitHubCodeViewer from "./GitHubCodeViewer";
import {
  FaPlay,
  FaPause,
  FaEye,
  FaEyeSlash,
  FaVideo,
  FaVideoSlash,
} from "react-icons/fa";
import { WasdControls } from "./WasdControls";
import CameraController, { cameraSetups } from "./CameraController";
import useAssetsLoader from "./useAssetsLoader";
import PreloadTexturesOnScene from "./PreloadTexturesOnScene";
import useStore from "./useStore";
import { useShallow } from "zustand/react/shallow";
import { assets_prefix, env_map } from "./constants";
import { isIOS, isMobile } from "react-device-detect";

const webGL2Available = isWebGL2Available();
interface ToggleButtonProps {
  onClick: () => void;
  isActive: boolean;
  children: React.ReactNode;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  onClick,
  isActive,
  children,
}) => {
  return (
    <button
      onClick={onClick}
      className={`toggle-button ${isActive ? "active" : ""}`}
    >
      <div className="button-icon">{isActive ? <FaEye /> : <FaEyeSlash />}</div>
      {children}
    </button>
  );
};

const REPORT_URL = `https://yoshuabengio.org/wp-content/uploads/2024/08/FlexHEG-Interim-Report_August-2024.pdf`;

interface ActionButtonProps {
  onClick: () => void;
  isRunning: boolean;
  children: React.ReactNode;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  isRunning,
  children,
}) => {
  return (
    <button onClick={onClick} className="action-button">
      <div className="button-icon play">
        {isRunning ? <FaPause /> : <FaPlay />}
      </div>
      {children}
    </button>
  );
};

interface CameraButtonProps {
  onClick: () => void;
  isActive: boolean;
  children: React.ReactNode;
}

const CameraButton: React.FC<CameraButtonProps> = ({
  onClick,
  isActive,
  children,
}) => {
  return (
    <button
      onClick={onClick}
      className={`camera-button ${isActive ? "active" : ""}`}
    >
      <div className="button-icon">
        {isActive ? <FaVideo /> : <FaVideoSlash />}
      </div>
      {children}
    </button>
  );
};

const LoadingOverlay = () => {
  const [connectionSlow, setConnectionSlow] = React.useState(false);

  const [isFinishedLoadingTextures, texturesPreloadedOnScene] = useStore(
    useShallow((state: any) => [
      state.isFinishedLoadingTextures,
      state.texturesPreloadedOnScene,
    ]),
  );

  React.useEffect(() => {
    // @ts-ignore
    if (navigator.connection) {
      // @ts-ignore
      const connection = navigator.connection.effectiveType;

      if (
        connection === "2g" ||
        connection === "slow-2g" ||
        connection === "3g"
      ) {
        setConnectionSlow(true);
        // Adjust your site behavior (e.g., reduce image quality, disable animations, etc.)
      }
    }
  }, []);

  return isFinishedLoadingTextures && (isMobile || texturesPreloadedOnScene) ? (
    <></>
  ) : (
    <div className="loading-overlay">
      {connectionSlow ? (
        <p style={{ color: "red" }}>Connection might be slow.</p>
      ) : (
        <></>
      )}
      {webGL2Available ? (
        isFinishedLoadingTextures ? (
          <>Preparing...</>
        ) : (
          <>Downloading...</>
        )
      ) : (
        <>This browser doesn't support this 3D model :(</>
      )}
    </div>
  );
};

const EXCLUDED_DEPTH_OBJECTS = ["Red Waves", "Blue Waves", "Fuse Signal"];

function SceneWithModel() {
  const modelRef = useRef<THREE.Group>(null);

  const [highlightedParts] = useStore(
    useShallow((state: any) => [state.highlightedParts]),
  );

  const applyAnyHighlight = highlightedParts.length > 0;

  const isObjectOrAncestorHighlighted = (obj: THREE.Object3D): boolean => {
    if (
      obj.userData?.name &&
      highlightedParts.some((part: any) => obj.userData.name.includes(part))
    ) {
      return true;
    }
    if (obj.parent) {
      return isObjectOrAncestorHighlighted(obj.parent);
    }
    return false;
  };

  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.traverse((object) => {
        // @ts-ignore
        if (object.isMesh) {
          const mesh = object as THREE.Mesh;
          mesh.frustumCulled = false;
          if (mesh.material) {
            // Clone the material to ensure each mesh has its own instance
            if (Array.isArray(mesh.material)) {
              mesh.material = mesh.material.map((m) => m.clone());
            } else {
              mesh.material = mesh.material.clone();
            }

            // Now modify the material
            const material = Array.isArray(mesh.material)
              ? mesh.material[0]
              : mesh.material;
            material.transparent = true;
            material.needsUpdate = true;
            // @ts-ignore
            if (material.metalness) {
              // @ts-ignore
              // material.metalness = Math.min(0.98, material.metalness);
            }
            // console.log(
            //   "checking",
            //   object.name,
            //   "against",
            //   highlightedParts,
            //   highlightedParts.some((part) => object.name.includes(part))
            // );

            if (applyAnyHighlight && isObjectOrAncestorHighlighted(object)) {
              material.opacity = 1;
            } else {
              material.opacity = applyAnyHighlight ? 0.1 : 1;
            }

            if (
              material.opacity !== 1 ||
              (EXCLUDED_DEPTH_OBJECTS.includes(material.name) &&
                material.opacity === 1)
            ) {
              material.polygonOffset = true;
              material.polygonOffsetFactor = -0.1;
              material.depthWrite = false;
              material.side = THREE.DoubleSide;
            } else {
              // material.polygonOffset = true;
              // material.polygonOffsetFactor = -0.1;
              material.depthWrite = true;
              mesh.renderOrder = 3;
              material.side = THREE.DoubleSide;
            }
          }
        }
      });
    }
  }, [applyAnyHighlight, highlightedParts]);

  return (
    <Center position={[-5, -1, 3]}>
      <Model topLevelRef={modelRef} scale={[1, 1, 1]} />
    </Center>
  );
}

function App() {
  const cameraControls: any = useRef<CameraControls | undefined>(); // TODO type

  const [
    showEnclosure,
    setShowEnclosure,
    showFoam,
    setShowFoam,
    fanStates,
    playBreachAnimation,
    playDisableAnimation,
    isRadioActive,
    isDieCoverOpen,
    isUnplugged,
    currentCamera,
    setCurrentCamera,
    highlightedParts,
    setHighlightedParts,
    togglePower,
    toggleFan,
    toggleRadio,
    toggleDieCover,
    togglePlayBreach,
    toggleSelfDisable,
    switchCamera,
  ] = useStore(
    useShallow((state: any) => [
      state.showEnclosure,
      state.setShowEnclosure,
      state.showFoam,
      state.setShowFoam,
      state.fanStates,
      state.playBreachAnimation,
      state.playDisableAnimation,
      state.isRadioActive,
      state.isDieCoverOpen,
      state.isUnplugged,
      state.currentCamera,
      state.switchCamera,
      state.highlightedParts,
      state.setHighlightedParts,
      state.togglePower,
      state.toggleFan,
      state.toggleRadio,
      state.toggleDieCover,
      state.togglePlayBreach,
      state.toggleSelfDisable,
      state.switchCamera,
    ]),
  );

  useAssetsLoader();

  return (
    <div className="app-wrapper">
      <div className="App">
        <header>
          <h1>Guaranteeable Chips Prototype v0.1</h1>
          <p className="subtitle">
            This site visualises an end-to-end prototype of a cluster of AI
            accelerators augmented with secure coprocessors, implementing the{" "}
            <a href={REPORT_URL} target="_blank" rel="noopener noreferrer">
              flexible, hardware-enabled guarantees ("flexHEGs") framework
            </a>
            . The software and hardware stacks are open source.
          </p>
          <p className="subtitle">
            Explore the prototype by moving around the model,{" "}
            {!isMobile ? "hovering over" : "tapping on"} the diagram, or playing
            any of the attached actions. There's also a recorded video of the
            implementation.
          </p>
        </header>

        <div className="wrapper">
          <section className="canvas-container">
            {webGL2Available ? (
              <Canvas
                gl={{
                  precision: "highp",
                  toneMapping: THREE.CineonToneMapping,
                }}
              >
                <CameraController
                  currentCamera={currentCamera}
                  cameraControls={cameraControls}
                />

                {/* Preloads all textures into GPU Memory, disabled until image compression.*/}
                {isMobile ? <></> : <PreloadTexturesOnScene />}
                <CameraControls maxDistance={25} ref={cameraControls} />
                {/* // camera={{ position: [0.05, 0.2, 0.1], near: 0.005, far: 10000 }} */}
                {/* <color attach="background" args={["#f0f0f0"]} /> */}
                <ambientLight intensity={0} />
                <Environment
                  // preset="studio"
                  //   environmentIntensity={1.0}
                  //   backgroundBlurriness={0.8}
                  files={assets_prefix + env_map}
                  // resolution={2048}
                >
                  <Lightformer
                    form="rect" // circle | ring | rect (optional, default = rect)
                    intensity={4} // power level (optional = 1)
                    color="white" // (optional = white)
                    position={[105, 100, 100]} // @ts-ignore
                    scale={[100, 50]} // Scale it any way you prefer (optional = [1, 1])
                    target={[-5, -1, 3]}
                  />
                  <Lightformer
                    form="rect" // circle | ring | rect (optional, default = rect)
                    intensity={6.5} // power level (optional = 1)
                    color="white" // (optional = white)
                    position={[-5, 100, 3]} // @ts-ignore
                    scale={[100, 20]} // Scale it any way you prefer (optional = [1, 1])
                    target={[-5, -1, 3]}
                    rotation={[Math.PI, 0, 0]}
                  />
                  <Lightformer
                    form="rect" // circle | ring | rect (optional, default = rect)
                    intensity={1} // power level (optional = 1)
                    color="white" // (optional = white)
                    position={[105, -100, 100]} // @ts-ignore
                    scale={[100, 50]} // Scale it any way you prefer (optional = [1, 1])
                    target={[-5, -1, 3]}
                  />
                </Environment>
                <SceneWithModel />
                <WasdControls
                  cameraControls={cameraControls}
                  movementSpeed={cameraSetups[currentCamera].movementSpeed}
                  panSpeed={cameraSetups[currentCamera].panSpeed}
                />
              </Canvas>
            ) : (
              <></>
            )}
            <div className="controls">
              <div className="controls-section-title">Show</div>
              <div className="controls-section">
                <ToggleButton
                  onClick={() => setShowEnclosure(!showEnclosure)}
                  isActive={showEnclosure}
                >
                  Enclosure lid
                </ToggleButton>
                <ToggleButton
                  onClick={() => setShowFoam(!showFoam)}
                  isActive={showFoam}
                >
                  Metal foam
                </ToggleButton>
              </div>
              <div className="controls-section-title">Actions</div>
              <div className="controls-section">
                <ActionButton
                  onClick={() => togglePower(!isUnplugged)}
                  isRunning={!isUnplugged}
                >
                  {isUnplugged ? "Plug" : "Unplug"} power
                </ActionButton>
                <ActionButton onClick={toggleRadio} isRunning={isRadioActive}>
                  {isRadioActive ? "Stop" : "Start"} radio
                </ActionButton>
                <ActionButton
                  onClick={toggleDieCover}
                  isRunning={isDieCoverOpen}
                >
                  {isDieCoverOpen ? "Close" : "Open"} die cover
                </ActionButton>
                <ActionButton
                  onClick={togglePlayBreach}
                  isRunning={playBreachAnimation}
                >
                  {playBreachAnimation ? "Stop breach" : "Breach enclosure"}
                </ActionButton>
                <ActionButton
                  onClick={toggleSelfDisable} // TODO
                  isRunning={playDisableAnimation}
                >
                  {playDisableAnimation ? "[cannot be undone]" : "Self-disable"}
                </ActionButton>
                {/* <ActionButton
                onClick={() => toggleFan([0, 1, 2])}
                isRunning={fanStates[0]}
              >
                {fanStates[0] ? "Disconnect" : "Connect"} power
              </ActionButton> */}
                {isUnplugged ? (
                  <></>
                ) : (
                  <ActionButton
                    onClick={() => {
                      toggleFan([0, 1, 2]);
                    }}
                    isRunning={fanStates[0]}
                  >
                    {fanStates[0] ? "Stop" : "Start"} fans
                  </ActionButton>
                )}
              </div>

              <div className="controls-section-title">Camera</div>
              <div className="controls-section">
                {isMobile ? (
                  <div className="camera-help-text">
                    Rotate: 1 finger <br /> Move: 2 fingers <br /> Zoom: pinch
                  </div>
                ) : (
                  <div className="camera-help-text">
                    Rotate & zoom: mouse Move: WASD keys Up/down: QE <br /> Run:
                    shift
                  </div>
                )}
                <CameraButton
                  onClick={() => switchCamera("enclosure")}
                  isActive={currentCamera === "enclosure"}
                >
                  Enclosure
                </CameraButton>
                <CameraButton
                  onClick={() => switchCamera("system")}
                  isActive={currentCamera === "system"}
                >
                  System
                </CameraButton>
                <CameraButton
                  onClick={() => switchCamera("chipDie")}
                  isActive={currentCamera === "chipDie"}
                >
                  Chip die
                </CameraButton>
                <CameraButton
                  onClick={() => switchCamera("insideChip")}
                  isActive={currentCamera === "insideChip"}
                >
                  Inside chip
                </CameraButton>
              </div>
            </div>
            <LoadingOverlay />
          </section>

          <section>
            <Diagram
              highlightedParts={highlightedParts}
              setHighlightedParts={setHighlightedParts}
              switchCamera={setCurrentCamera}
              isDieCoverOpen={isDieCoverOpen}
              toggleDieCover={toggleDieCover}
            />
          </section>
        </div>
        {/* <section className="github-code-section">
        <h2>GitHub Code Examples</h2>
        <GitHubCodeViewer
          owner="google"
          repo="skywater-pdk"
          path="scripts/python-skywater-pdk/skywater_pdk/corners.py"
        />
        <GitHubCodeViewer
          owner="google"
          repo="skywater-pdk"
          path="scripts/python-skywater-pdk/skywater_pdk"
        />
      </section> */}

        <div className="video-container">
          <video
            controls
            preload="auto" // Prevents loading the video until user interacts
            style={{ width: "100%", maxHeight: "500px" }} // Adjust as needed
          >
            <source
              src="https://peter.website/tmp/Demo1WithCaptions.mp4"
              type="video/mp4"
            />
            Your browser does not support videos :( Maybe try this link on
            another device? https://peter.website/tmp/Demo1WithCaptions.mp4
          </video>
        </div>

        <section className="explanation"></section>
        <section className="metadata">
          {/* <div id="about">about</div> */}
          <div id="about-text">
            <p>
              The prototype system was designed and built in August 2024, by a
              team in California, with high-level direction from David Dalrymple
              (davidad) and funding from Atlas Computing. This site was made by
              Jacob Lagerros, together with Flavius Stefeliga and Radical
              Software.
            </p>
          </div>
          <section className="files">
            <ul>
              <li>
                <a href={REPORT_URL} target="_blank" rel="noopener noreferrer">
                  Technical report on Flexible, hardware-enabled guarantees
                  (PDF)
                </a>
              </li>
              <li>
                <a href="https://github.com/example/interactive-3d-models">
                  Prototype code (GitHub) [TODO]
                </a>
              </li>
              <li>
                <a href="/files/3d-model-loader.js" download>
                  Demo website code (GitHub) [TODO]
                </a>
              </li>
              <li>
                <a href="https://airtable.com/appV45JWQQiF94pSI/shrk7tI67VhZMmc6u">
                  Materials (Airtable list)
                </a>
              </li>
              <li>
                <a href="/files/pcb_layout.kicad_pcb" download>
                  PCB layout (KiCAD PCB file)
                </a>
              </li>
              <li>
                <a href="/files/pcb_layout.kicad_pro" download>
                  PCB layout (KiCAD project file)
                </a>
              </li>
            </ul>
          </section>
          {/* <div className="metadata-columns">
              <div className="column">
                <h3>authors</h3>
                <ul>
                  <li>John Doe</li>
                  <li>Jane Smith</li>
                </ul>
              </div>
              <div className="column">
                <h3>affiliations</h3>
                <ul>
                  <li>Organisation Inc</li>
                  <li>Acronym Research</li>
                </ul>
              </div>
              <div className="column">
                <p>
                  <em>Aug 23rd, 2024</em>
                </p>
              </div>
            </div> */}
        </section>
      </div>
    </div>
  );
}

export default App;
